import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, TextField } from '@mui/material';

import { InfoTag } from '../../../../components/Tags';
import { NEW_SITE_ID, SuggestionReason, filterSites } from '../../../utils/webflowDeployer';
import texts from '../texts.json';
import { OptionContent, OptionDomain } from '../styles';

const NEW_SITE_OPTION = { id: NEW_SITE_ID, isNewSite: true };

/*
  Module-level so their identity is stable: MUI resets the typed text whenever these change while
  the input is not focused.
*/
const getOptionLabel = (option) => (option.isNewSite ? texts.picker.newSite : option.name);
const isOptionEqualToValue = (option, selected) => option.id === selected.id;

/*
  MUI's Autocomplete directly rather than CoachTools' AutoComplete wrapper: the wrapper sorts the
  options by label in place and only reads its value once, and this picker needs "Deploy as new
  site" pinned first and a pre-selection that arrives after the sites have loaded.
*/
const SitePicker = ({
  sites,
  isLoading,
  loadError,
  suggestedSite,
  selectedSiteId,
  onChange,
}) => {
  const siteOptions = useMemo(() => {
    // A suggested site the list does not hold (still loading, or failed to) stays selectable.
    const isSuggestionListed = !suggestedSite || sites.some(({ id }) => id === suggestedSite.id);
    return isSuggestionListed ? sites : [{ ...suggestedSite, url: '', customDomain: null }, ...sites];
  }, [
    sites,
    suggestedSite,
  ]);

  const options = useMemo(() => [NEW_SITE_OPTION, ...siteOptions], [siteOptions]);
  const value = options.find(({ id }) => id === selectedSiteId) || NEW_SITE_OPTION;
  const suggestedSiteId = suggestedSite?.id;
  const suggestionLabel = suggestedSite?.reason === SuggestionReason.SAME_NAME
    ? texts.picker.sameName
    : texts.picker.lastDeployed;

  // The new-site option is not a search result: it stays available whatever is typed.
  const filterOptions = useCallback((_, { inputValue }) => [
    NEW_SITE_OPTION,
    ...filterSites(siteOptions, inputValue),
  ], [siteOptions]);

  const renderOption = (props, option) => (
    <li {...props} key={option.id}>
      {option.isNewSite ? texts.picker.newSite : (
        <OptionContent>
          <span>
            {option.name}
            {option.id === suggestedSiteId && <InfoTag>{suggestionLabel}</InfoTag>}
          </span>
          <OptionDomain>{option.customDomain || option.url}</OptionDomain>
        </OptionContent>
      )}
    </li>
  );

  const helperText = loadError || (value.id === suggestedSiteId ? suggestionLabel : '');

  return (
    <Autocomplete
      options={options}
      value={value}
      onChange={(_, option) => onChange(option.id)}
      filterOptions={filterOptions}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      renderOption={renderOption}
      renderInput={(params) => (
        <TextField
          {...params}
          label={texts.picker.label}
          helperText={helperText}
          error={!!loadError}
          size="small"
        />
      )}
      loading={isLoading}
      loadingText={texts.picker.loading}
      noOptionsText={texts.picker.noMatch}
      disableClearable
      size="small"
    />
  );
};

SitePicker.propTypes = {
  sites: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    url: PropTypes.string,
    customDomain: PropTypes.string,
  })).isRequired,
  isLoading: PropTypes.bool.isRequired,
  loadError: PropTypes.string,
  suggestedSite: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    reason: PropTypes.oneOf(Object.values(SuggestionReason)),
  }),
  selectedSiteId: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

SitePicker.defaultProps = {
  loadError: '',
  suggestedSite: null,
};

export default SitePicker;
